import { useGetFile } from '@/hooks/useFiles';
import { useMy } from '@/hooks/useMy';

/**
 * observationPrefix builds the convention path prefix under which augustus writes
 * a webpage's reconnaissance files: observations/augustus/<hex(webpageKey)>/. The
 * key segment is the hex of the type=llm Webpage key — the SAME lossless,
 * byte-identical encoding the backend uses (Go hex.EncodeToString over []byte(key);
 * here TextEncoder → hex) — so this reconstructs the exact prefix the backend
 * wrote to, with no ambiguous sanitization. Keying by the webpage (not its parent
 * WebApplication) keeps multiple LLM endpoints under one application distinct.
 * Listing this prefix is how the UI discovers the files (attributes are no longer
 * used).
 */
function hexEncode(s: string): string {
  return Array.from(new TextEncoder().encode(s), b =>
    b.toString(16).padStart(2, '0')
  ).join('');
}

export function observationPrefix(webpageKey: string): string {
  return `observations/augustus/${hexEncode(webpageKey)}/`;
}

/** Coerce a useGetFile result (parsed object or raw JSON string) into an inventory. */
function parseInventory(v: unknown): McpInventory | undefined {
  if (!v) return undefined;
  if (typeof v === 'string') {
    try {
      return JSON.parse(v) as McpInventory;
    } catch {
      return undefined;
    }
  }
  if (typeof v === 'object') return v as McpInventory;
  return undefined;
}

/** Coerce a useGetFile result into the recon-observations bundle (array of envelopes). */
function parseObservations(v: unknown): ReconObservation[] {
  let parsed: unknown = v;
  if (typeof v === 'string') {
    try {
      parsed = JSON.parse(v);
    } catch {
      return [];
    }
  }
  return Array.isArray(parsed) ? (parsed as ReconObservation[]) : [];
}

/** Coerce a useGetFile result (JSON string[] or parsed array) into a string list. */
function parseStringArray(v: unknown): string[] {
  let parsed: unknown = v;
  if (typeof v === 'string') {
    try {
      parsed = JSON.parse(v);
    } catch {
      return [];
    }
  }
  return Array.isArray(parsed)
    ? parsed.map(String).filter(s => s.trim().length > 0)
    : [];
}

/** Does a webpage's generator_configs declare an `mcp` generator? */
function generatorConfigsHaveMcp(gc: unknown): boolean {
  // The API serializes Webpage.GeneratorConfigs ([]GeneratorConfig) as a JSON
  // array of objects. Accept a raw JSON string too, defensively, in case a
  // caller passes the unparsed Neo4j property.
  let configs: unknown = gc;
  if (typeof gc === 'string') {
    try {
      configs = JSON.parse(gc);
    } catch {
      return false;
    }
  }
  return (
    Array.isArray(configs) &&
    configs.some(
      c =>
        typeof c === 'object' &&
        c !== null &&
        (c as { type?: string }).type === 'mcp'
    )
  );
}

/**
 * deriveIsMcpServer — pure test of whether an asset is an MCP server, from two
 * signals: its type=llm webpage's generator_configs and whether augustus has
 * already enumerated an MCP inventory for it.
 *
 * True when the webpage declares an `mcp` generator (set at seed/fingerprint
 * time, so this holds pre-scan) OR augustus has already produced an MCP inventory
 * observation (`hasMcpInventory`). A plain LLM chat endpoint has a type=llm
 * webpage but an openai/anthropic generator and no inventory, so it reads as
 * not-MCP.
 */
export function deriveIsMcpServer(
  webpage: { key?: string; generator_configs?: unknown } | undefined,
  hasMcpInventory: boolean
): boolean {
  // A confirmed MCP inventory is decisive on its own — augustus only enumerates
  // one for an MCP server — so it wins even if the type=llm webpage query returned
  // nothing yet. Only then fall back to the webpage's generator-config signal.
  if (hasMcpInventory) return true;
  if (!webpage?.key) return false;
  return generatorConfigsHaveMcp(webpage.generator_configs);
}

/**
 * useLLMRecon — reads the augustus reconnaissance observations for an asset from
 * files under a convention path (observations/augustus/<hex(assetKey)>/), listed
 * by prefix — augustus no longer emits attributes. The generic
 * `recon-observations.json` bundle holds every observation (the MCP inventory,
 * identifiers, and any future recon module); `recon-errors.json` holds the recon
 * failures. This hook returns a parsed, panel-ready shape for the LLM Recon tab.
 */

export interface McpToolAnnotations {
  read_only?: boolean;
  destructive?: boolean;
  idempotent?: boolean;
  open_world?: boolean;
  title?: string;
}

export interface McpTool {
  name: string;
  title?: string;
  description?: string;
  annotations?: McpToolAnnotations;
}

export interface McpResource {
  uri?: string;
  uri_template?: string;
  name?: string;
  description?: string;
  mime_type?: string;
}

export interface McpPromptArg {
  name: string;
  description?: string;
  required?: boolean;
}

export interface McpPrompt {
  name: string;
  title?: string;
  description?: string;
  arguments?: McpPromptArg[];
}

export interface McpInventory {
  transport?: string;
  protocol_version?: string;
  instructions?: string;
  server_name?: string;
  server_version?: string;
  tools?: McpTool[];
  resources?: McpResource[];
  resource_templates?: McpResource[];
  prompts?: McpPrompt[];
  counts?: {
    tools?: number;
    resources?: number;
    resource_templates?: number;
    prompts?: number;
  };
}

export interface LLMReconSummary {
  serverName?: string;
  serverVersion?: string;
  transport?: string;
  protocolVersion?: string;
  instructions?: string;
  tools: number;
  resources: number;
  prompts: number;
}

/**
 * ReconObservation is the generic augustus reconnaissance envelope
 * (pkg/output.Observation): a stable `type` slug, the thing observed (`target`),
 * the producing recon module (`source`), and a type-specific `data` payload.
 * The UI renders these dynamically — a registered per-type view when one exists,
 * a structured fallback otherwise — so new recon modules surface with no UI work.
 */
export interface ReconObservation {
  type: string;
  target?: string;
  source?: string;
  data?: unknown;
}

export interface LLMRecon {
  present: boolean;
  discoveredBy: string;
  inventory?: McpInventory;
  summary: LLMReconSummary;
  /** Every reconnaissance observation emitted for this asset (all types). */
  observations: ReconObservation[];
}

export interface UseLLMReconResult {
  recon: LLMRecon;
  isFetching: boolean;
  /** Key of the type=llm webpage under this WebApplication — the augustus scan target. */
  targetWebpageKey?: string;
  /**
   * True when this asset is an MCP server: its type=llm webpage carries an `mcp`
   * generator config, or augustus has already enumerated an MCP inventory for it.
   * Used to gate the MCP-specific LLM Recon tab so it doesn't show for plain
   * (non-MCP) LLM endpoints or ordinary web applications.
   */
  isMcpServer: boolean;
  /**
   * Reconnaissance failures augustus surfaced for this asset (a recon module
   * that failed to construct or errored mid-run), from the `recon-errors.json`
   * file under the observation convention path (empty after a clean re-run).
   * Non-empty means recon ran degraded — the panel warns rather than showing a
   * bare "no reconnaissance" state.
   */
  reconErrors: string[];
}

export function useLLMRecon(assetKey: string): UseLLMReconResult {
  // The augustus target is the type=llm webpage under this WebApplication.
  const { data: webpages = [], status: webpagesStatus } = useMy(
    {
      resource: 'webpage',
      query: {
        node: {
          labels: ['Webpage'],
          filters: [{ field: 'type', operator: '=', value: 'llm' }],
          relationships: [
            {
              label: 'HAS_WEBPAGE',
              source: {
                labels: ['Asset'],
                filters: [
                  { field: 'key', operator: '=', value: assetKey || '' },
                ],
              },
            },
          ],
        },
      },
    },
    { enabled: Boolean(assetKey) }
  );
  // Prefer the MCP webpage (one carrying an `mcp` generator config) over an
  // arbitrary first type=llm endpoint, so a WebApplication with several LLM
  // endpoints still targets the MCP one for recon (Codex review).
  const llmWebpages = webpages as {
    key?: string;
    generator_configs?: unknown;
  }[];
  const targetWebpage =
    llmWebpages.find(w => generatorConfigsHaveMcp(w.generator_configs)) ??
    llmWebpages[0];
  const targetWebpageKey = targetWebpage?.key;

  // Discover the webpage's augustus reconnaissance files by listing the convention
  // prefix (no attributes). The prefix is the hex of the WEBPAGE key — matching
  // the path the compute worker wrote to — so multiple LLM endpoints under one
  // WebApplication don't overwrite each other's inventory.
  const prefix = targetWebpageKey ? observationPrefix(targetWebpageKey) : '';
  const { data: reconFiles = [], status: filesStatus } = useMy(
    { resource: 'file', query: prefix },
    { enabled: Boolean(targetWebpageKey) }
  );
  const findFile = (basename: string) =>
    (reconFiles as { name?: string }[]).find(f =>
      f.name?.endsWith(`/${basename}`)
    )?.name;

  // Generic observation bundle: every observation augustus emitted (mcp.inventory,
  // mcp.identifiers, any future recon module) in one JSON file.
  const observationsName = findFile('recon-observations.json');
  const { data: obsData, isFetching: obsFetching } = useGetFile<
    ReconObservation[] | string
  >(
    { name: observationsName || '' },
    { enabled: Boolean(observationsName), retry: 0 }
  );
  const observations = parseObservations(obsData);

  // The MCP summary card is sourced from the mcp.inventory observation within the
  // bundle — no separate inventory file/attribute.
  const inventory = parseInventory(
    observations.find(o => o.type === 'mcp.inventory')?.data
  );

  // Recon failures, in a stable-path file (a clean re-run overwrites it and
  // clears the warning); the file is a JSON string[] (empty on success).
  const reconErrorsName = findFile('recon-errors.json');
  const { data: reconErrData, isFetching: reconErrFetching } = useGetFile<
    string[] | string
  >(
    { name: reconErrorsName || '' },
    { enabled: Boolean(reconErrorsName), retry: 0 }
  );
  const reconErrors = parseStringArray(reconErrData);

  const isMcpServer = deriveIsMcpServer(targetWebpage, Boolean(inventory));

  return {
    targetWebpageKey,
    isMcpServer,
    reconErrors,
    isFetching:
      (webpagesStatus === 'pending' && Boolean(assetKey)) ||
      (filesStatus === 'pending' && Boolean(targetWebpageKey)) ||
      Boolean(obsFetching) ||
      Boolean(reconErrFetching),
    recon: {
      present: observations.length > 0 || reconErrors.length > 0,
      discoveredBy: 'augustus',
      inventory,
      observations,
      summary: {
        serverName: inventory?.server_name,
        serverVersion: inventory?.server_version,
        transport: inventory?.transport,
        protocolVersion: inventory?.protocol_version,
        instructions: inventory?.instructions,
        tools: inventory?.counts?.tools ?? 0,
        resources: inventory?.counts?.resources ?? 0,
        prompts: inventory?.counts?.prompts ?? 0,
      },
    },
  };
}
